/**
 * Return URL Handlers
 *
 * Utilities for getting, storing, and clearing return URLs
 */

import { isValidReturnUrl } from "./urlValidation.js";
import { DEFAULT_REDIRECTS } from "./redirectConfig.js";

/**
 * Get return URL from various sources (query param, session storage, state)
 * @param {URLSearchParams} searchParams - URL search parameters
 * @param {Object} locationState - React Router location state
 * @returns {string|null} - Valid return URL or null
 */
export const getReturnUrl = (searchParams = null, locationState = null) => {
  // Priority order: query param > location state > session storage
  let returnUrl = null;

  // 1. Check query parameter
  if (searchParams) {
    returnUrl =
      searchParams.get("returnUrl") ||
      searchParams.get("redirect") ||
      searchParams.get("return");
  }

  // 2. Check React Router location state (for same-origin)
  if (!returnUrl && locationState?.from) {
    returnUrl = `${window.location.origin}${locationState.from}`;
  }

  // 3. Check session storage as fallback
  if (!returnUrl) {
    returnUrl = sessionStorage.getItem("authReturnUrl");
  }

  return returnUrl;
};

/**
 * Get safe redirect URL with validation and fallback
 * @param {URLSearchParams} searchParams - URL search parameters
 * @param {Object} locationState - React Router location state
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @returns {string} - Safe redirect URL
 */
export const getSafeRedirectUrl = (
  searchParams = null,
  locationState = null,
  isAuthenticated = false,
) => {
  const returnUrl = getReturnUrl(searchParams, locationState);

  if (returnUrl && isValidReturnUrl(returnUrl)) {
    // Clean up session storage since we're using the URL
    sessionStorage.removeItem("authReturnUrl");
    return returnUrl;
  }

  // Fallback to default redirect
  const fallback = isAuthenticated
    ? DEFAULT_REDIRECTS.authenticated
    : DEFAULT_REDIRECTS.unauthenticated;

  return fallback;
};

/**
 * Store return URL in session storage (backup method)
 * @param {string} returnUrl - URL to store
 */
export const storeReturnUrl = (returnUrl) => {
  if (returnUrl && isValidReturnUrl(returnUrl)) {
    sessionStorage.setItem("authReturnUrl", returnUrl);
  }
};

/**
 * Clear stored return URL
 */
export const clearReturnUrl = () => {
  sessionStorage.removeItem("authReturnUrl");
};
